/* Actual GPU shader. Transparent outside the smoke; four original brand masks. */
(() => {
 'use strict';
 const portal=document.getElementById('HeaderCloudWebGL04');
 const header=document.querySelector('#header-component[data-header-04]');
 const logo=header?.querySelector('.header-logo');
 const section=header?.closest('.header-section');
 if(!portal||!logo||portal.dataset.bound==='true')return;
 portal.dataset.bound='true';
 const canvas=portal.querySelector('canvas'),reduced=matchMedia('(prefers-reduced-motion: reduce)');
 // Independent Muschio pass over the negative, preserving its luminosity.
 const hue=document.getElementById('HeaderCloudHue04'),hueCanvas=hue?.querySelector('canvas'),hueContext=hueCanvas?.getContext('2d');
 let gl,program,buffer,texture,uniforms,image,frame=0,lastDraw=0,start=0,elapsed=0,active=false,disposed=false,lost=false;
 let journeyActive=false;
 let desktopMoving=false,desktopObserved=false,desktopStop=0,desktopTone=null;
 const desktop=()=>innerWidth>=990;
 const economical=typeof navigator!=='undefined'&&(navigator.connection?.saveData||/^(slow-)?2g$/.test(navigator.connection?.effectiveType||''));
 let quality=economical?1:Math.min(2,Math.max(1.5,typeof devicePixelRatio==='number'?devicePixelRatio:1.5)),lag=0;
 const vertex='attribute vec2 aPosition;varying vec2 vUV;void main(){vUV=(aPosition+1.)*.5;gl_Position=vec4(aPosition,0.,1.);}';
 const fragment=`precision highp float;
 varying vec2 vUV;uniform sampler2D uAtlas;uniform vec2 uResolution;uniform float uTime,uMark,uCloud,uWindScale;
 float hash(vec3 p){p=fract(p*.3183099+vec3(.1,.2,.3));p*=17.;return fract(p.x*p.y*p.z*(p.x+p.y+p.z));}
 float noise(vec3 p){vec3 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(mix(hash(i),hash(i+vec3(1,0,0)),f.x),mix(hash(i+vec3(0,1,0)),hash(i+vec3(1,1,0)),f.x),f.y),mix(mix(hash(i+vec3(0,0,1)),hash(i+vec3(1,0,1)),f.x),mix(hash(i+vec3(0,1,1)),hash(i+vec3(1,1,1)),f.x),f.y),f.z);}
 float fb(vec3 p){float v=0.,a=.57;for(int i=0;i<3;i++){v+=a*noise(p);p=p*2.07+vec3(7.3,11.1,3.7);a*=.46;}return v;}
 float ease(float a,float b,float x){float v=clamp((x-a)/(b-a),0.,1.);return v*v*(3.-2.*v);}
 float mark(float index,vec2 uv){if(min(uv.x,uv.y)<0.||max(uv.x,uv.y)>1.)return 0.;return texture2D(uAtlas,(vec2(mod(index,2.),1.-floor(index/2.))+uv)*.5).r;}
 float carriedMark(float index,vec2 uv,float amount,float sec){
  // The ink itself stretches into wisps and contracts into the next mask.
  vec3 q=vec3(uv*4.2,sec*.26);vec2 warp=vec2(fb(q),fb(q+17.3))-.48;
  vec2 at=(uv-.5)/vec2(1.+amount*.85,1.+amount*.25)+.5;
  at+=warp*amount*.65;
  float radius=amount*.055;
  float ink=mark(index,at)*.28;
  ink+=(mark(index,at+vec2(radius,0.))+mark(index,at-vec2(radius,0.)))*.18;
  ink+=(mark(index,at+vec2(0.,radius))+mark(index,at-vec2(0.,radius)))*.18;
  return ink;
 }
 void main(){
  float sec=mod(uTime,16.),index=floor(sec/4.),phase=mod(sec,4.);
  float swallow=ease(.95,2.20,phase),reveal=1.-ease(2.15,3.90,phase);
  float birth=ease(.95,1.95,phase)*(1.-ease(2.55,3.90,phase));
  float aspect=uResolution.x/uResolution.y;
  vec2 p=(vUV-vec2(.5,.5))*vec2(aspect,1.);
  vec2 uv=p/max(uMark,.001)+.5;
  float next=mod(index+1.,4.);
  float outgoing=carriedMark(index,uv,swallow,sec)*(1.-ease(1.95,2.85,phase));
  float incoming=carriedMark(next,uv,reveal,sec)*ease(1.75,3.35,phase);
  float ink=max(outgoing*pow(1.-birth,1.8),incoming*(1.-reveal*.75));
  float shape=max(carriedMark(index,uv,1.,sec)*(1.-ease(2.,3.,phase)),carriedMark(next,uv,1.,sec)*ease(1.7,2.7,phase));
  // Smoke remains on the logo's baseline, with only a restrained leftward flow.
  vec2 wind=vec2((-.035+.012*sin(sec*1.7))*birth,.003*sin(sec*1.3)*birth)*uWindScale;
  float optical=0.;vec2 xy=(p-wind)/(max(uCloud,.001)*vec2(1.15,.45));
  if(birth>.001&&length(xy)<1.8){
   for(int i=0;i<10;i++){
    vec3 q=vec3(xy,-1.3+(float(i)+.5)*.26);
    vec3 flow=q*1.8+vec3(sec*.26,-sec*.035,sec*.09);
    vec3 warp=vec3(fb(flow),fb(flow+19.4),fb(flow+43.7))-.48;
    vec3 w=q+warp*1.25;
    float envelope=1.-smoothstep(.75,1.45,length(w*vec3(.95,1.05,1.)));
    float billow=fb(w*3.2+vec3(sec*.035,0.,0.));
    float folds=exp(-abs(billow-.46)*25.);
    float core=exp(-dot(w,w)*5.)*.18;
    optical+=envelope*(.025+folds*.72+core)*.26;
   }
  }
  // The smoke field gathers into the incoming strokes before becoming solid.
  float gather=ease(2.10,3.85,phase);
  float target=carriedMark(next,uv,reveal,sec);
  float veil=mix(.28+.72*smoothstep(0.,.18,shape),target,gather);
  float cloud=(1.-exp(-optical*4.2))*birth*veil;
  float alpha=1.-(1.-ink*.96)*(1.-cloud*.94);
  // Premultiplied output keeps empty pixels RGBA(0,0,0,0), including Safari.
  float a=clamp(alpha,0.,.98);gl_FragColor=vec4(vec3(a),a);
 }`;
 function fallback(){logo.removeAttribute('data-cloud-source-04');document.body.removeAttribute('data-header-cloud-04');portal.hidden=true;portal.dataset.ready='false';if(hue){hue.hidden=true;hue.dataset.ready='false';}}
 function tint(){
  const moving=desktop()&&desktopObserved?desktopMoving:header.dataset['contrastMotion-04']==='moving';
  portal.dataset.inverseState=moving?'moving':'rest';
  portal.dataset.contrastTone=desktop()&&desktopTone?desktopTone:logo.dataset?.['contrastTone-04']==='white'?'white':'black';
  if(!moving){if(hue){hue.hidden=true;hue.dataset.ready='false';}return;}
  if(!hueContext)return;
  hue.style.cssText=portal.style.cssText;
  if(hueCanvas.width!==canvas.width||hueCanvas.height!==canvas.height){hueCanvas.width=canvas.width;hueCanvas.height=canvas.height;}
  hueContext.globalCompositeOperation='copy';hueContext.drawImage(canvas,0,0);
  hueContext.globalCompositeOperation='source-in';hueContext.fillStyle='#808D45';hueContext.fillRect(0,0,hueCanvas.width,hueCanvas.height);
  hue.hidden=false;hue.dataset.ready='true';
 }
 function pause(){if(active){elapsed+=(performance.now()-start)/1000;active=false;}cancelAnimationFrame(frame);frame=0;}
 function visible(){
  if(document.hidden||lost||disposed||!program)return false;
  if(header.dataset['menuOpen-04']==='true'||document.querySelector('dialog[open],[data-nav04-drawer][open]')||header.querySelector('.nav04--desktop .nav04__label:is(:hover,:focus-visible)'))return false;
  const r=logo.getBoundingClientRect(),style=getComputedStyle(section||header);
  return r.width>1&&r.height>1&&r.bottom>0&&r.top<innerHeight&&style.display!=='none'&&style.visibility!=='hidden'&&Number(style.opacity)>.05;
 }
 function geometry(){
  const r=logo.getBoundingClientRect(),width=Math.min(innerWidth,520),height=desktop()?240:520;
  portal.style.left=(r.left+r.width/2-width/2)+'px';portal.style.top=(r.top+r.height/2-height*.50)+'px';
  portal.style.width=width+'px';portal.style.height=height+'px';
  const renderQuality=desktop()&&!economical?Math.max(1.5,quality):quality;
  const scale=Math.min(renderQuality,Math.sqrt((desktop()?500000:innerWidth<750?240000:400000)/(width*height)));
  const w=Math.max(1,Math.round(width*scale)),h=Math.max(1,Math.round(height*scale));
  if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h;gl.viewport(0,0,w,h);}
  gl.uniform2f(uniforms.uResolution,w,h);
  gl.uniform1f(uniforms.uWindScale,520/height);
  const mark=Math.min(190,Math.max(130,r.width*1.45));
  gl.uniform1f(uniforms.uMark,mark/height);gl.uniform1f(uniforms.uCloud,Math.min(mark*.65,width*.24)/height);
 }
 function draw(ms){
  try {
  if(journeyActive){pause();return;}
  if(!visible()){pause();fallback();return;}
  frame=requestAnimationFrame(draw);
  if(ms-lastDraw<32)return;
  if(lastDraw&&ms-lastDraw>60)lag++;else lag=Math.max(0,lag-1);
  if(lag>=5){quality=Math.max(1,quality*.85);lag=0;}
  lastDraw=ms;geometry();
  gl.uniform1f(uniforms.uTime,reduced.matches?0:elapsed+(ms-start)/1000);
  gl.clear(gl.COLOR_BUFFER_BIT);gl.drawArrays(gl.TRIANGLES,0,6);
  tint();
  portal.hidden=false;portal.dataset.ready='true';logo.setAttribute('data-cloud-source-04','active');document.body.setAttribute('data-header-cloud-04','ready');
  if(reduced.matches){pause();}
  }catch(error){lost=true;pause();fallback();release();portal.dataset.error='render';}
 }
 function sync(){
  if(journeyActive)return;
  if(!visible()){pause();fallback();return;}
  if(!active){start=performance.now();active=true;lastDraw=0;frame=requestAnimationFrame(draw);}
 }
 function release(){if(!gl)return;if(texture)gl.deleteTexture(texture);if(buffer)gl.deleteBuffer(buffer);if(program)gl.deleteProgram(program);texture=buffer=program=null;}
 function build(){
  const compile=(type,source)=>{const s=gl.createShader(type);gl.shaderSource(s,source);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS)){const message=gl.getShaderInfoLog(s);gl.deleteShader(s);throw Error(message)}return s;};
  const vs=compile(gl.VERTEX_SHADER,vertex);let fs;
  try{fs=compile(gl.FRAGMENT_SHADER,fragment);}catch(error){gl.deleteShader(vs);throw error;}
  program=gl.createProgram();gl.attachShader(program,vs);gl.attachShader(program,fs);gl.linkProgram(program);gl.deleteShader(vs);gl.deleteShader(fs);
  if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw Error(gl.getProgramInfoLog(program));
  gl.useProgram(program);buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
  const p=gl.getAttribLocation(program,'aPosition');gl.enableVertexAttribArray(p);gl.vertexAttribPointer(p,2,gl.FLOAT,false,0,0);
  uniforms=Object.fromEntries(['uAtlas','uResolution','uTime','uMark','uCloud','uWindScale'].map(n=>[n,gl.getUniformLocation(program,n)]));
  texture=gl.createTexture();gl.activeTexture(gl.TEXTURE0);gl.bindTexture(gl.TEXTURE_2D,texture);gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
  gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,image);gl.uniform1i(uniforms.uAtlas,0);gl.clearColor(0,0,0,0);portal.dataset.renderer='webgl';sync();
 }
 function lostContext(event){event.preventDefault();lost=true;pause();fallback();}
 function restored(){lost=false;try{release();build();}catch{release();fallback();}}
 function pageHide(event){pause();fallback();if(!event.persisted)dispose();}
 const watchdog=window.setInterval?.(()=>{if(active&&!document.hidden&&lastDraw&&performance.now()-lastDraw>1500){lost=true;pause();fallback();release();portal.dataset.error='stalled';}},750);
 function dispose(){
  clearTimeout(desktopStop);
  window.clearInterval?.(watchdog);
  disposed=true;pause();fallback();release();observer.disconnect();
  document.removeEventListener('scroll',scroll,true);document.removeEventListener('visibilitychange',sync);document.removeEventListener('pointermove',sync);
  document.removeEventListener('04:cart-smoke-state',journeyState);
  window.removeEventListener('resize',sync);window.removeEventListener('pageshow',sync);window.removeEventListener('pagehide',pageHide);reduced.removeEventListener('change',sync);
  canvas.removeEventListener('webglcontextlost',lostContext);canvas.removeEventListener('webglcontextrestored',restored);
 }
 function journeyState(event){journeyActive=event.detail?.active===true;if(journeyActive)pause();else sync();}
 // Desktop product galleries may scroll inside their own container. Keep their
 // cloud motion local; the working mobile contrast engine remains untouched.
 let sampleContext;
 function desktopRestTone(){
  const r=logo.getBoundingClientRect(),x=r.left+r.width/2,y=r.top+r.height/2;
  const stack=document.elementsFromPoint?.(x,y)||[];
  for(const node of stack){
   if(node.closest?.('#header-group,.header-cloud04,.v5-header-moss-hue,[popover],dialog'))continue;
   const style=getComputedStyle(node);if(style.visibility==='hidden'||Number(style.opacity)<.5)continue;
   const rect=node.getBoundingClientRect();
   if(node.tagName==='IMG'&&node.complete&&node.naturalWidth&&rect.width&&rect.height){
    try{
     sampleContext ||= document.createElement('canvas').getContext('2d',{willReadFrequently:true});
     if(sampleContext){const c=sampleContext.canvas;c.width=c.height=1;
      const scale=style.objectFit==='cover'?Math.max(rect.width/node.naturalWidth,rect.height/node.naturalHeight):Math.min(rect.width/node.naturalWidth,rect.height/node.naturalHeight);
      const sx=(x-rect.left-(rect.width-node.naturalWidth*scale)/2)/scale,sy=(y-rect.top-(rect.height-node.naturalHeight*scale)/2)/scale;
      if(sx>=0&&sy>=0&&sx<node.naturalWidth&&sy<node.naturalHeight){sampleContext.drawImage(node,sx,sy,1,1,0,0,1,1);const c=sampleContext.getImageData(0,0,1,1).data;if(c[3]>128)return .2126*c[0]+.7152*c[1]+.0722*c[2]<127.5?'white':'black';}
     }
    }catch{} // Cross-origin media falls back to the shared sampler's last tone.
   }
   const c=style.backgroundColor?.match(/[\d.]+/g)?.map(Number);
   if(c&&c.length>=3&&(c.length<4||c[3]>.5))return .2126*c[0]+.7152*c[1]+.0722*c[2]<127.5?'white':'black';
  }
  return logo.dataset?.['contrastTone-04']==='white'?'white':'black';
 }
 function scroll(event){
  if(desktop()&&event?.target&&!document.querySelector('dialog[open],[data-nav04-drawer][open]')&&!event.target.closest?.('#header-group,dialog,[data-nav04-drawer],.cart-drawer__content')){
   desktopObserved=true;desktopMoving=true;desktopTone=null;clearTimeout(desktopStop);
   desktopStop=setTimeout(()=>{desktopMoving=false;desktopTone=desktopRestTone();tint();sync();},180);
   tint();
  }
  sync();
 }
 document.addEventListener('04:cart-smoke-state',journeyState);
 const observer=new MutationObserver(sync);observer.observe(header,{attributes:true,attributeFilter:['data-menu-open-04','data-sticky-state','data-contrast-motion-04','style','class']});
 observer.observe(logo,{attributes:true,attributeFilter:['data-contrast-tone-04']});
 document.addEventListener('scroll',scroll,{capture:true,passive:true});document.addEventListener('visibilitychange',sync);document.addEventListener('pointermove',sync,{passive:true});
 window.addEventListener('resize',sync,{passive:true});window.addEventListener('pageshow',sync);window.addEventListener('pagehide',pageHide);reduced.addEventListener('change',sync);
 canvas.addEventListener('webglcontextlost',lostContext);canvas.addEventListener('webglcontextrestored',restored);
 try{
  gl=canvas.getContext('webgl',{alpha:true,premultipliedAlpha:true,antialias:false,depth:false,stencil:false,powerPreference:'low-power'});
  if(!gl)throw Error('WebGL unavailable');
  image=new Image();image.crossOrigin='anonymous';image.onload=()=>{if(disposed)return;try{build();}catch(error){portal.dataset.error=String(error.message||error);release();fallback();}};image.onerror=()=>fallback();image.src=portal.dataset.atlas;
 }catch(error){portal.dataset.error=String(error.message||error);fallback();}
})();


